import { useCallback, useMemo } from 'react';
import { getDate } from 'app/common/lib/util/date';
import {
  ProductFeatures,
  BillingDates,
  ExpirationDates,
} from '@trello/product-features';
import { deserializeJSONString } from '@trello/graphql';
import { useSunsetGold } from 'app/src/components/GoldSunset/useSunsetGold';
import { useGoldPremiumFreeTrialBannerQuery } from './GoldPremiumFreeTrialBannerQuery.generated';
import { useDismissGoldPremiumFreeTrialMutation } from './DismissGoldPremiumFreeTrialMutation.generated';
import { ApolloError } from '@apollo/client';
import { memberId } from '@trello/session-cookie';

import moment from 'moment';
import Cookies from 'js-cookie';

interface GoldPremiumFreeTrialData {
  loading: boolean;
  error?: ApolloError | Error;
  isGoldUserEligibleForFreeTrial: boolean;
  hasDismissedFromBoardView: boolean;
  dismissBanner: () => void;
}

export const GTM_LAUNCH_DATE = new Date('8/24/2021');
export const GOLD_PROMO_FREE_TRIAL_PAGE = '/page/gold-promo-free-trial';
const messageId = 'nusku.gold-premium-free-trial-banner';

export const useGoldPremiumFreeTrial = (): GoldPremiumFreeTrialData => {
  const { data, loading, error } = useGoldPremiumFreeTrialBannerQuery({
    variables: {
      memberId: memberId || '',
    },
  });

  const previouslyUsedFreeTrial = Boolean(
    data?.member?.goldSunsetFreeTrialIdOrganization,
  );

  const paidAccount = {
    products: data?.member?.paidAccount?.products ?? [],
    standing: data?.member?.paidAccount?.standing ?? -1,
    billingDates:
      typeof data?.member?.paidAccount?.billingDates === 'object'
        ? data.member.paidAccount.billingDates
        : deserializeJSONString<BillingDates>(
            data?.member?.paidAccount?.billingDates,
          ) ?? {},
    expirationDates:
      typeof data?.member?.paidAccount?.expirationDates === 'object'
        ? data.member.paidAccount.expirationDates
        : deserializeJSONString<ExpirationDates>(
            data?.member?.paidAccount?.expirationDates,
          ) ?? {},
  };

  const { goldSunsetEnabled, goldIsActive } = useSunsetGold(paidAccount);

  const isUserWithoutTeamlessBoards = !data?.member?.boards?.some(
    ({ memberships, enterpriseOwned, idOrganization }) =>
      !idOrganization &&
      !enterpriseOwned &&
      memberships?.find(
        ({ idMember, memberType }) =>
          idMember === data?.member?.id && memberType === 'admin',
      ),
  );

  const isConfirmedMember = Boolean(data?.member?.confirmed);

  const isEnterpriseMember = Boolean(
    data?.member?.enterprises?.find(
      (ent) => ent.id === data?.member?.idEnterprise,
    )?.isRealEnterprise,
  );

  const gtmDate =
    Cookies.get('simulateGTMDate') || GTM_LAUNCH_DATE.toISOString();

  const freeTrialOfferEndDate = moment(gtmDate).add(31, 'day');

  const {
    ixSubscriptionProductId: cancelledProduct,
    dtCancelled: cancelledDate,
  } = data?.member?.paidAccount?.previousSubscription ?? {};

  const goldIsCancelledPostGTM = Boolean(
    cancelledProduct &&
      ProductFeatures.isGoldProduct(cancelledProduct) &&
      moment(cancelledDate)?.isAfter(gtmDate, 'day'),
  );

  const freeTrialOfferHasPassed = freeTrialOfferEndDate.isBefore(
    getDate(),
    'day',
  );

  const isGoldUserEligibleForFreeTrial =
    isConfirmedMember &&
    !isEnterpriseMember &&
    goldSunsetEnabled &&
    (goldIsActive || goldIsCancelledPostGTM) &&
    isUserWithoutTeamlessBoards &&
    !freeTrialOfferHasPassed &&
    !previouslyUsedFreeTrial;

  const oneTimeMessagesDismissed = useMemo(
    () => data?.member?.oneTimeMessagesDismissed ?? [],
    [data?.member?.oneTimeMessagesDismissed],
  );
  const hasDismissedFromBoardView = Boolean(
    oneTimeMessagesDismissed?.includes(messageId),
  );

  const [
    dismiss,
    { loading: isDismissing },
  ] = useDismissGoldPremiumFreeTrialMutation();

  const dismissBanner = useCallback(() => {
    if (isDismissing || hasDismissedFromBoardView) {
      return;
    }

    dismiss({
      variables: {
        memberId: memberId ?? 'me',
        messageId,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        addOneTimeMessagesDismissed: {
          id: 'me',
          oneTimeMessagesDismissed: [...oneTimeMessagesDismissed, messageId],
          __typename: 'Member',
        },
      },
    });
  }, [
    dismiss,
    isDismissing,
    hasDismissedFromBoardView,
    oneTimeMessagesDismissed,
  ]);

  return {
    loading,
    error,
    isGoldUserEligibleForFreeTrial,
    hasDismissedFromBoardView,
    dismissBanner,
  };
};
